import "@/App.css";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, Users, Sparkles, UtensilsCrossed, Music, Camera, Gift, Heart, Cake, PartyPopper, Briefcase } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ------------------- Experience content data ------------------- */
const EXPERIENCES = {
  "romantic-date-night": {
    title: "Romantic Date Night in a Private Theatre",
    tagline: "Cozy setups, candlelit decor & a whole theatre just for the two of you",
    hero: "https://images.unsplash.com/photo-1542204165-65bf26472b9b?auto=format&fit=crop&w=1600&q=80",
    badge: "Most Booked",
    price: "2,499",
    hrs: "3 HRS",
    ppl: "2-6 People",
    intro: "Surprise your partner with an unforgettable evening — a handpicked movie, dreamy decor, fairy lights, gourmet food and complete privacy. No crowds, no distractions, just the two of you on a giant screen with premium Dolby sound.",
    features: [
      { icon: <Heart size={20} />, t: "Themed Decor", d: "Rose petals, fairy lights & cozy cushions" },
      { icon: <UtensilsCrossed size={20} />, t: "Gourmet Food", d: "Candlelit dining with in-room service" },
      { icon: <Music size={20} />, t: "Dolby Sound", d: "Crystal-clear 7.1 premium audio" },
      { icon: <Camera size={20} />, t: "Photoshoot", d: "Capture your moments with decor" },
    ],
    types: [
      { t: "Anniversary Celebration", d: "Celebrate your love with a personalised setup, cake & a special video message" },
      { t: "Proposal Experience", d: "The perfect cinematic setting to pop the big question — we'll set the stage" },
      { t: "First Date Surprise", d: "Low-key, private, magical — leave a lasting impression from the very start" },
      { t: "Couple Movie Night", d: "Your favourite film, your favourite food, zero interruptions" },
    ],
  },
  "birthday-celebration": {
    title: "Grand Birthday Celebration in Private Theatre",
    tagline: "Make birthdays magical with decor, cake, food & your favourite movie",
    hero: "https://images.unsplash.com/photo-1533294455009-a77b7557d2d1?auto=format&fit=crop&w=1600&q=80",
    badge: "Most Booked",
    price: "3,499",
    hrs: "3 HRS",
    ppl: "2-10 People",
    intro: "Book a hassle-free, premium and budget-friendly birthday celebration in just a few clicks. Grand balloon decor, your favourite cake, delicious food and a massive screen to play your home video or favourite movie — the birthday star gets the full spotlight.",
    features: [
      { icon: <Sparkles size={20} />, t: "Grand Decoration", d: "Balloon arches, LED letters & themed setup" },
      { icon: <Cake size={20} />, t: "Cakes & Gifts", d: "Premium cakes, bouquets & surprise gifts" },
      { icon: <UtensilsCrossed size={20} />, t: "Food & Beverages", d: "Curated menu with in-room dining" },
      { icon: <Camera size={20} />, t: "Photoshoot", d: "Professional photos & video highlights" },
    ],
    types: [
      { t: "Kid's Birthday Party", d: "Cartoon themes, animated movies & a safe, supervised private space" },
      { t: "Couple Birthday Celebration", d: "Romantic birthday setups with cozy decor & personalised messages" },
      { t: "Family Birthday Bash", d: "A private, safe, perfect spot for the whole family to celebrate together" },
      { t: "Friends Birthday Celebration", d: "Dance, sing & enjoy — the kind of space you'll fall in love with" },
    ],
  },
  "friends-movie-night": {
    title: "Friends Movie Night in a Private Screen",
    tagline: "Fun, food, loud laughs & your whole squad on the big screen",
    hero: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1600&q=80",
    badge: "Trending",
    price: "2,199",
    hrs: "3 HRS",
    ppl: "1-12 People",
    intro: "A private theatre rented just for your gang — binge your favourite franchise, cheer for a match, or roast a cult-classic together. Loud allowed, dance allowed, zero shushing. Order unlimited snacks & drinks straight to your seats.",
    features: [
      { icon: <Users size={20} />, t: "Squad Seating", d: "Comfy recliners for up to 12 friends" },
      { icon: <UtensilsCrossed size={20} />, t: "Unlimited Snacks", d: "Popcorn, pizzas, burgers & mocktails" },
      { icon: <Music size={20} />, t: "Play Anything", d: "Your OTT login, match, game or playlist" },
      { icon: <PartyPopper size={20} />, t: "Zero Rules", d: "Sing, dance, shout — it's your space" },
    ],
    types: [
      { t: "Reunion Night", d: "Catch up with the old gang over a nostalgic movie & dinner" },
      { t: "Bachelor / Bachelorette", d: "Private, wild & tailor-made send-off for the bride/groom-to-be" },
      { t: "Match Screening", d: "Cricket, F1, football — cheer on the 20-ft screen together" },
      { t: "Binge Marathon", d: "Three hours, multiple episodes, endless snacks" },
    ],
  },
  "corporate-screening": {
    title: "Corporate Screening & Team Events",
    tagline: "Professional, exclusive & perfect for offsites, launches and team nights",
    hero: "https://images.unsplash.com/photo-1517604931442-7e0c8ed2963c?auto=format&fit=crop&w=1600&q=80",
    badge: "Premium",
    price: "3,999",
    hrs: "3 HRS",
    ppl: "6-20 People",
    intro: "Host your next product launch, team offsite, training screening or client event in a fully private theatre. HDMI / laptop input, premium sound, catered F&B, and a calm environment built for collaboration — all with GST invoicing.",
    features: [
      { icon: <Briefcase size={20} />, t: "HDMI / Laptop Input", d: "Plug & play — present or screen from any device" },
      { icon: <Users size={20} />, t: "Team Seating", d: "Up to 20 seats with premium recliners" },
      { icon: <UtensilsCrossed size={20} />, t: "Curated Catering", d: "Breakfast, lunch or hi-tea packages" },
      { icon: <Gift size={20} />, t: "GST Invoicing", d: "Corporate bills & reimbursement-ready receipts" },
    ],
    types: [
      { t: "Product Launch", d: "A cinematic reveal for your next big product or brand film" },
      { t: "Team Offsite", d: "A movie + team lunch — the easiest offsite you'll ever plan" },
      { t: "Training Screening", d: "Private room to screen internal training or onboarding videos" },
      { t: "Client Event", d: "Impress clients with a premium, memorable private screening" },
    ],
  },
  "anniversary-celebration": {
    title: "Anniversary Celebration in Private Theatre",
    tagline: "Celebrate your love story on the big screen",
    hero: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1600&q=80",
    price: "2,999",
    hrs: "3 HRS",
    ppl: "2-8 People",
    intro: "Turn your anniversary into a cinematic milestone — a custom decor setup, your wedding video on the giant screen, a handpicked cake and in-room dining. Perfect for 1st, 5th, 10th or 25th — we scale the magic to the milestone.",
    features: [
      { icon: <Heart size={20} />, t: "Personalised Decor", d: "Monogram, photos & mood lighting" },
      { icon: <Camera size={20} />, t: "Wedding Video Replay", d: "Relive the day on the 20-ft screen" },
      { icon: <Cake size={20} />, t: "Anniversary Cake", d: "Custom design, your flavour" },
      { icon: <UtensilsCrossed size={20} />, t: "Candlelit Dining", d: "Private in-room multi-course service" },
    ],
    types: [
      { t: "First Anniversary", d: "The paper one — a personalised love-letter reel on the big screen" },
      { t: "Silver / Gold Milestone", d: "Invite family and honour the journey together" },
      { t: "Surprise Setup", d: "Pull off the perfect anniversary surprise without lifting a finger" },
      { t: "Destination-style", d: "A private, decorated space that feels like a getaway — in your city" },
    ],
  },
  "proposal-experience": {
    title: "Proposal Experience — The Perfect 'Yes'",
    tagline: "A cinematic stage for the biggest moment of your life",
    hero: "https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?auto=format&fit=crop&w=1600&q=80",
    badge: "Exclusive",
    price: "4,499",
    hrs: "2 HRS",
    ppl: "2 People",
    intro: "A full private theatre, a custom proposal reel on the big screen, fairy lights, rose petals, candles and silence on cue. We sync the moment down to the second so all you have to do is get down on one knee.",
    features: [
      { icon: <Heart size={20} />, t: "Custom Proposal Reel", d: "Your photos + 'Will you marry me?' on screen" },
      { icon: <Sparkles size={20} />, t: "Dreamy Decor", d: "Petals, fairy lights, candles & a ring-stage" },
      { icon: <Camera size={20} />, t: "Discreet Photographer", d: "Capture the 'yes' without breaking the moment" },
      { icon: <UtensilsCrossed size={20} />, t: "Celebration Dinner", d: "Champagne & a private 3-course meal after" },
    ],
    types: [
      { t: "Classic Proposal", d: "Petals, ring, screen, tears — the timeless setup" },
      { t: "Nostalgic Reel", d: "Your first date, first trip, first everything — on the big screen" },
      { t: "Family Reveal", d: "Let the family watch live via a private stream after the yes" },
      { t: "Re-proposal / Vow Renewal", d: "A second-time-around moment, bigger than the first" },
    ],
  },
  "bachelor-bachelorette": {
    title: "Bachelor / Bachelorette Private Night",
    tagline: "A wild, private send-off — your squad, your rules",
    hero: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=1600&q=80",
    price: "3,299",
    hrs: "3 HRS",
    ppl: "4-15 People",
    intro: "Book out the theatre for your bachelor or bachelorette — themed decor, the bride's/groom's favourite movie, a custom roast reel, unlimited F&B and a dance-floor energy. Loud, private and totally yours.",
    features: [
      { icon: <PartyPopper size={20} />, t: "Themed Decor", d: "Bride-to-be / groom-to-be signage & props" },
      { icon: <Camera size={20} />, t: "Roast & Memory Reel", d: "Send us the photos — we'll cut the reel" },
      { icon: <Music size={20} />, t: "Play Anything", d: "Movie, playlist, karaoke — it's your night" },
      { icon: <UtensilsCrossed size={20} />, t: "Party Platter", d: "Unlimited snacks, drinks & mocktails" },
    ],
    types: [
      { t: "Bachelorette Night", d: "Sash, sparkles, playlist — the ultimate girls' night in" },
      { t: "Bachelor Party", d: "Match + movie + munchies + zero judgement" },
      { t: "Couple Bachelor", d: "One theatre, both squads, before the big day" },
      { t: "Hen / Stag at Home-city", d: "No travel, full privacy, all vibes" },
    ],
  },
  "match-screening": {
    title: "Match Screening on a 20-ft Private Screen",
    tagline: "Cricket, F1, football, UFC — on your own private big screen",
    hero: "https://images.unsplash.com/photo-1508247967583-7d982ea01526?auto=format&fit=crop&w=1600&q=80",
    badge: "Sports",
    price: "1,999",
    hrs: "3 HRS",
    ppl: "2-15 People",
    intro: "A private theatre, your match on a 20-ft screen with Dolby sound, recliners, unlimited snacks & cold drinks. Cheer as loud as you want — the room is yours. Perfect for IPL finals, World Cup nights, F1 Sundays and UFC pay-per-views.",
    features: [
      { icon: <PartyPopper size={20} />, t: "Cheer Loud", d: "No shushing — it's your private room" },
      { icon: <UtensilsCrossed size={20} />, t: "Match Menu", d: "Wings, nachos, pizzas, beer-style mocktails" },
      { icon: <Music size={20} />, t: "Premium Sound", d: "Stadium-level 7.1 Dolby audio" },
      { icon: <Users size={20} />, t: "Squad Seating", d: "Up to 15 recliners for the whole gang" },
    ],
    types: [
      { t: "IPL / World Cup Final", d: "The finals deserve a 20-ft screen and a private room" },
      { t: "F1 Sunday", d: "Every lap on the big screen with the commentary cranked up" },
      { t: "Football Derby", d: "UCL nights, El Clásico, NFL playoffs — all covered" },
      { t: "UFC / WWE PPV", d: "Pay-per-view events with popcorn and zero buffering" },
    ],
  },
  "binge-marathon": {
    title: "Binge Marathon — Your Series, Your Screen",
    tagline: "Three hours, your OTT, recliners, unlimited snacks",
    hero: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?auto=format&fit=crop&w=1600&q=80",
    price: "1,799",
    hrs: "3 HRS",
    ppl: "1-8 People",
    intro: "Log into any OTT (Netflix, Prime, Disney+ Hotstar, JioCinema) and binge your favourite series on a 20-ft screen in total privacy. Recliners, unlimited popcorn, and the kind of calm you can't get at home.",
    features: [
      { icon: <Music size={20} />, t: "Bring Your OTT", d: "We provide the screen, you log in" },
      { icon: <UtensilsCrossed size={20} />, t: "Snack Bar", d: "Bottomless popcorn, nachos & drinks" },
      { icon: <Users size={20} />, t: "Recliner Seating", d: "Premium recliners for up to 8" },
      { icon: <Sparkles size={20} />, t: "Ambient Lighting", d: "Soft lights tuned for long binges" },
    ],
    types: [
      { t: "Series Finale Night", d: "Book out the room for the big finale your group waited for" },
      { t: "Film Marathon", d: "Trilogy or franchise day — back-to-back on the big screen" },
      { t: "Anime Binge", d: "Dub/Sub, subtitles, your call — anime nights done right" },
      { t: "Documentary Night", d: "A quiet, private room for doc-lovers & their favourite deep-dives" },
    ],
  },
};

/* ------------------- Shared visuals ------------------- */
const Section = ({ children, className = "" }) => (
  <section className={`max-w-[1280px] mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);

const GoldButton = ({ children, className = "", ...props }) => (
  <button
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold tracking-wide px-8 py-4 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition-all shadow-[0_4px_24px_rgba(212,175,116,0.35)] ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-xl border border-[#3a2a14]/70 bg-gradient-to-b from-[#14100a] to-[#0d0905] ${className}`}>
    {children}
  </div>
);

/* ------------------- Page ------------------- */
const Experience = () => {
  const { slug } = useParams();
  const [dbData, setDbData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/experiences/${slug}`)
      .then((r) => { if (alive) { setDbData(r.data); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [slug]);

  const data = dbData
    ? {
        title: dbData.name,
        tagline: dbData.sub || "",
        hero: dbData.image,
        badge: dbData.badge || "",
        price: dbData.base_price ? Number(dbData.base_price).toLocaleString("en-IN") : "—",
        hrs: dbData.hrs || "3 HRS",
        ppl: dbData.ppl || "",
        intro: dbData.description || "",
        features: [],
        types: [],
      }
    : EXPERIENCES[slug];

  if (!loaded) {
    return (
      <div className="min-h-screen bg-[#0a0604] text-[#a89878] flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0a0604] text-[#f4d089] flex flex-col items-center justify-center gap-4">
        <h1 className="font-display text-3xl">Experience not found</h1>
        <a href="/" className="text-[#f4d089] underline" data-testid="exp-not-found-home">Go back home</a>
      </div>
    );
  }

  const goBook = () => {
    window.location.href = `/book?experience=${encodeURIComponent(data.title)}`;
  };

  return (
    <div className="min-h-screen bg-[#0a0604] text-[#e8dcc0]" data-testid={`experience-page-${slug}`}>
      {/* Top bar */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0a0604]/85 border-b border-[#d4a574]/15">
        <Section className="flex items-center justify-between py-4">
          <a href="/" className="flex items-center" data-testid="cinevibe-logo">
  <div className="w-16 h-16 rounded-full overflow-hidden border border-[#d4a574]/40 flex items-center justify-center bg-[#0a0604]">
    <img src="/images/logo.png" alt="CineVibe logo" className="w-full h-full object-cover" />
  </div>
</a>
          <GoldButton onClick={goBook} data-testid="exp-header-book-btn" className="text-sm px-5 py-2.5">BOOK NOW</GoldButton>
        </Section>
      </header>

      {/* Hero */}
      <div className="relative h-[420px] sm:h-[520px] w-full overflow-hidden">
        <img src={data.hero} alt={data.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0604] via-[#0a0604]/55 to-[#0a0604]/30" />
        <Section className="absolute inset-x-0 bottom-0 pb-10">
          {data.badge && (
            <span className="inline-block bg-[#f4d089] text-[#1a0f06] text-[11px] font-bold tracking-wider px-3 py-1 rounded mb-4">
              {data.badge}
            </span>
          )}
          <h1 className="font-display text-white text-3xl sm:text-5xl leading-tight max-w-3xl">{data.title}</h1>
          <p className="text-[#d8cdb3] mt-3 max-w-2xl">{data.tagline}</p>
        </Section>
      </div>

      {/* Intro */}
      <Section className="py-16">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-white">{data.title.split(" in ")[0]}</h2>
          <p className="text-[#bfb393] mt-4 leading-relaxed text-[15px]">{data.intro}</p>
        </div>

        {/* Features */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {data.features.map((f) => (
            <Card key={f.t} className="p-6 text-center hover:border-[#d4a574]/50 transition">
              <div className="w-12 h-12 mx-auto rounded-full bg-[#1a0f06] border border-[#d4a574]/30 text-[#f4d089] flex items-center justify-center">
                {f.icon}
              </div>
              <h4 className="text-white mt-4 font-semibold">{f.t}</h4>
              <p className="text-xs text-[#a89878] mt-2">{f.d}</p>
            </Card>
          ))}
        </div>

        {/* CTA #1 */}
        <div className="flex justify-center mt-12">
          <GoldButton onClick={goBook} data-testid="exp-mid-book-btn" className="text-base tracking-[0.15em]">
            BOOK YOUR PRIVATE THEATER NOW
          </GoldButton>
        </div>
      </Section>

      {/* Types */}
      <Section className="pb-16">
        <div className="text-center mb-10">
          <h3 className="font-display text-3xl text-white">
            What you can celebrate <span className="text-[#f4d089]">*</span>
          </h3>
          <p className="text-[#a89878] mt-3 max-w-xl mx-auto">
            Every setup is custom-built for your occasion. Here are the most-loved variants of this experience.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {data.types.map((ty) => (
            <Card key={ty.t} className="p-6 hover:border-[#d4a574]/50 transition">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-[#f4d089] mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-white font-semibold">{ty.t}</h4>
                  <p className="text-xs text-[#a89878] mt-2 leading-relaxed">{ty.d}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <div className="border-t border-[#3a2a14]/60 bg-gradient-to-b from-[#0d0905] to-[#14100a]">
        <Section className="py-16 text-center">
          <h3 className="font-display text-3xl sm:text-4xl text-white">Ready to make it unforgettable?</h3>
          <p className="text-[#bfb393] mt-3 max-w-xl mx-auto">
            Instant confirmation. Premium decor. Private theatre.
          </p>
          <div className="flex justify-center mt-8">
            <GoldButton onClick={goBook} data-testid="exp-footer-book-btn" className="text-base tracking-[0.18em] flex items-center gap-2">
              BOOK A PRIVATE SCREEN NOW <ArrowRight size={18} />
            </GoldButton>
          </div>
        </Section>
      </div>

      <footer className="border-t border-[#3a2a14]/60 py-6 text-center text-xs text-[#a89878] leading-relaxed">
        © {new Date().getFullYear()} The CineVibe Private Screens & Café<br />
        BnBBuddy Property Management Services Pvt Ltd · GSTIN: 07AALCB5876H1Z8
      </footer>
    </div>
  );
};

export default Experience;
